import { Cable, CircleDot, GitFork, Link2, Server, Home } from 'lucide-react'
import { useEffect } from 'react'
import { api, type PathStep } from '../api'
import { useApp } from '../context'
import { budgetVerdict, fmtAge, fmtDb, fmtDbm, fmtM } from '../format'
import { useAsync } from '../hooks'
import { PanelShell } from './PanelShell'
import { Badge, Button, Callout, ErrorState, KeyValue, Loading, Section } from './ui'

const STEP_ICON: Record<PathStep['type'], typeof Cable> = { pon: Server, connection: Link2, fiber: Cable, splitter: GitFork, drop: Home }

export function TracePanel({ serviceId }: { serviceId: string }) {
  const { open, setHighlight, network } = useApp()
  const q = useAsync(() => api.trace(serviceId), [serviceId])

  useEffect(() => {
    if (q.data) setHighlight({ cableIds: q.data.cableIds, nodeIds: [q.data.napNodeId], tone: 'accent' })
  }, [q.data, setHighlight])

  if (!q.data) return q.error ? <ErrorState error={q.error} onRetry={q.reload} /> : <Loading />
  const { service: s, steps, budget: b, end } = q.data
  const v = budgetVerdict(b)
  const nap = network.nodes.find((n) => n.id === q.data!.napNodeId)

  return (
    <PanelShell
      title={s.label}
      subtitle={`${s.accountRef} · ${nap?.name ?? ''} port ${s.port}`}
      badge={<Badge tone={v.tone}>{v.label}</Badge>}
      actions={
        <Button size="sm" onClick={() => open({ kind: 'node', id: q.data!.napNodeId })}>
          Open {nap?.name}
        </Button>
      }
    >
      <Section title="Optical budget (1490 nm downstream)">
        {b ? (
          <>
            <KeyValue
              rows={[
                ['OLT launch power', fmtDbm(b.txPowerDbm)],
                ['Path loss (designed)', fmtDb(b.totalLossDb)],
                ['Expected at ONU', fmtDbm(b.expectedRxDbm)],
                [`Class ${q.data.opticClass} sensitivity`, fmtDbm(b.sensitivityDbm)],
                ['Margin', <span className={b.marginDb < 3 ? 'text-warn' : ''}>{fmtDb(b.marginDb)}</span>],
                [
                  'Measured at ONU',
                  b.measuredRxDbm !== undefined ? (
                    <span>
                      {fmtDbm(b.measuredRxDbm)} <span className="text-muted">({fmtAge(s.onu?.readAt)}, synthetic reading)</span>
                    </span>
                  ) : (
                    <span className="text-muted">No reading</span>
                  ),
                ],
                ['Measured − expected', b.deviationDb !== undefined ? <span className={b.deviates ? 'font-semibold text-danger' : ''}>{b.deviationDb > 0 ? '+' : ''}{fmtDb(b.deviationDb)}</span> : '—'],
              ]}
            />
            {v.hint && (
              <div className="mt-3">
                <Callout tone={v.tone} title={v.label}>
                  {v.hint}
                </Callout>
              </div>
            )}
          </>
        ) : (
          <Callout tone="danger" title="Path does not reach an OLT">
            {end.kind === 'open' ? `The fibre is open at ${end.nodeId ? network.nodes.find((n) => n.id === end.nodeId)?.name : 'an unknown node'}: a splice is missing there.` : `Trace ended with “${end.kind}”.`}
          </Callout>
        )}
      </Section>
      <Section title={`Path · ${steps.length} steps · ${fmtM(steps.at(-1)?.cumulativeLengthM ?? 0)} of fibre`}>
        <ol className="relative space-y-0">
          {steps.map((st, i) => {
            const Icon = STEP_ICON[st.type]
            const clickable = st.nodeId || st.cableId
            const body = (
              <>
                <span className="relative z-10 mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-border bg-surface">
                  {st.fiberColor ? (
                    <span className="h-3.5 w-3.5 rounded-full border border-black/20" style={{ background: st.fiberColor, boxShadow: `0 0 0 3px ${st.tubeColor}` }} aria-hidden />
                  ) : (
                    <Icon className="h-4 w-4 text-muted" aria-hidden />
                  )}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block font-medium">{st.title}</span>
                  <span className="block break-words text-xs text-muted">{st.detail}</span>
                </span>
                <span className="num shrink-0 text-right text-xs">
                  {st.lossDb > 0 && <span className="block">+{st.lossDb.toFixed(2)} dB</span>}
                  <span className="block text-muted">Σ {st.cumulativeLossDb.toFixed(2)} dB</span>
                  {st.lengthM > 0 && <span className="block text-muted">{fmtM(st.lengthM)}</span>}
                </span>
              </>
            )
            return (
              <li key={i} className="relative">
                {i < steps.length - 1 && <span className="absolute left-[13px] top-8 h-[calc(100%-1rem)] w-px bg-border" aria-hidden />}
                {clickable ? (
                  <button
                    className="flex w-full items-start gap-2 rounded-lg px-1 py-1.5 text-left text-sm hover:bg-surface-2"
                    onClick={() => open(st.cableId ? { kind: 'cable', id: st.cableId } : { kind: 'node', id: st.nodeId! })}
                  >
                    {body}
                  </button>
                ) : (
                  <div className="flex items-start gap-2 px-1 py-1.5 text-sm">{body}</div>
                )}
              </li>
            )
          })}
        </ol>
        <p className="mt-2 flex items-center gap-1 text-xs text-muted">
          <CircleDot className="h-3.5 w-3.5" aria-hidden /> Loss uses planning defaults (fibre 0.25 dB/km at 1490 nm, fusion 0.1 dB, connector 0.3 dB, 1:8 splitter 10.5 dB) unless a measured value is recorded.
        </p>
      </Section>
    </PanelShell>
  )
}
